import { ProtectedRoute } from '@components/Auth';
import Footer from '@components/Footer';
import Header from '@components/Header';
import { LoadingFallback } from '@components/Loading/AppLoaders';
import Sidebar from '@components/Sidebar';
import { CallbackSignIn, CallbackSignOut, PartnerRedirect, PartnerSubscriptionSuccess } from '@pages/AuthPages';
import CompanionsPage from '@pages/Companions';
import ContributorProfile from '@pages/ContributorProfile';
import CreationViewerPage from '@pages/CreationViewer';
import FavoritesPage from '@pages/Favorites';
import Feeds from '@pages/Feeds';
import Home from '@pages/Home';
import IndianMusicPage from '@pages/IndianMusic';
import IndianMusicLeaderboardPage from '@pages/IndianMusicLeaderboard';
import LeaderboardPage from '@pages/Leaderboard';
import MusicPage from '@pages/Music';
import AccountPage from '@pages/MyAccount/MyAccountPage';
import MyCreationsPage from '@pages/MyCreations';
import OldMusicsPage from '@pages/OldMusics';
import OldPlaylistsPage from '@pages/OldPlaylists';
import PlaylistsPage from '@pages/Playlists';
import { Features } from '../config';
// import Musicplayer from '@pages/Musicplayer';
import { useStationMode } from '@hooks/station';
import { PricingCheckoutPage } from '@pages/PricingCheckoutPage';
import SearchPage from '@pages/Search';
import SerenityWellnessPage from '@pages/SerenityWellness';
import SessionPage from '@pages/SessionPage';
import { AccessDenied } from '@pages/SessionPage/AccessDenied';
import StationGenerationPage from '@pages/SessionPage/StationGenerationPage';
import StationSpacesPage from '@pages/station/StationSpacesPage';
import SubscriptionPage from '@pages/SubscriptionPage';
import { TwinPage } from '@pages/TwinPage';
import WisdomPage from '@pages/Wisdom';
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

// Lazy load dev pages
const IconsDevPage = lazy(() => import('@pages/IconsDevPage'));
// Lazy load campaign landing pages
const OfferPage = lazy(() => import('@pages/OfferPage'));

// Check if running on localhost
const isLocalhost = window.location.hostname === 'localhost';
// Routes that should hide sidebar and header
export const ROUTES_WITHOUT_SIDEBAR_HEADER = [
  '/oldplaylists',
  '/twin',
  '/offers',
];

// Known non-session routes (used for session route detection)
const KNOWN_NON_SESSION_ROUTES = [
  'feeds',
  'favorites',
  'playlists',
  'my-creations',
  'musicforyou',
  'search',
  'companions',
  'serenity-wellness',
  'audiobooks',
  'indian-music',
  'indian-music-leaderboard',
  'leaderboard',
  'old-music',
  'oldplaylists',
  'account',
  'pricing',
  'pricing-checkout',
  'callbackSignIn',
  'callbackSignOut',
  'auth',
  'access-denied',
  'offers',
];

export const shouldHideSidebarAndHeader = (pathname: string): boolean => {
  /*const isAppNapsterAi =
    typeof window !== 'undefined' &&
    window.location.hostname === 'app.napster.ai';*/
  if (pathname.startsWith('/auth/partner')) return true;
  if (pathname.startsWith('/callbackSignIn')) return true;
  return ROUTES_WITHOUT_SIDEBAR_HEADER.some((route) =>
    pathname.startsWith(route),
  );
};

// Animation variants for page transitions
const pageVariants = {
  initial: {
    translateX: '100%',
    opacity: 0,
  },
  in: {
    translateX: '0%',
    opacity: 1,
  },
  out: {
    translateX: '0%',
    opacity: 1,
  },
};

// Fade-in animation variant
const fadeInVariant = {
  initial: {
    opacity: 0,
  },
  in: {
    opacity: 1,
  },
  out: {
    opacity: 1,
  },
};

// Wrapper component for animated pages
const AnimatedPage = ({
  children,
  isInitialLoad,
  isFade = false,
}: {
  children: React.ReactNode;
  isInitialLoad: boolean;
  isFade?: boolean;
}) => {
  return (
    <motion.div
      initial={isInitialLoad ? 'in' : 'initial'}
      animate="in"
      exit="out"
      variants={isFade ? fadeInVariant : pageVariants}
      transition={{ duration: 0.3, ease: 'easeInOut' }}
      className="absolute inset-0 w-full h-full">
      {children}
    </motion.div>
  );
};
// Main Layout - renders Sidebar, Header, content area, Footer
const MainLayout = ({
  hideFooter,
  hideHeader,
  hideSidebar,
}: {
  hideFooter: boolean;
  hideHeader: boolean;
  hideSidebar: boolean;
}) => {
  const location = useLocation();
  const isStationMode = useStationMode();

  return (
    <>
      {!hideSidebar && <Sidebar />}
      {!hideHeader && <Header />}
      <Suspense fallback={<LoadingFallback />}>
        <div
          className={`${
            location.pathname.startsWith('/player/') ||
            location.pathname.startsWith('/playlist/') ||
            location.pathname.startsWith('/creations/track/')
              ? ''
              : shouldHideSidebarAndHeader(location.pathname)
                ? // Standalone pages with no sidebar/header/footer — give the
                  // wrapper full-viewport sizing so `h-full` / `justify-center`
                  // inside the page can resolve correctly.
                  'absolute inset-0 w-full h-full font-[NapsterSans]'
                : isStationMode
                  ? 'absolute sm:w-full sm:left-0 w-full h-full bottom-[0px] left-0 sm:top-[65px] top-[60px] transition-all duration-500 ease-in-out font-[NapsterSans]'
                  : 'absolute sm:w-[calc(100%-215px)] sm:left-[215px] w-full h-full bottom-[0px] left-0 sm:top-0 top-[60px] transition-all duration-500 ease-in-out font-[NapsterSans]'
          }`}>
          <Outlet />
        </div>
      </Suspense>
      {!hideFooter && <Footer />}
    </>
  );
};

// Session Layout - renders only Header, no Sidebar/Footer
const SessionLayout = () => {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <Header />
      <div>
        <Outlet />
      </div>
    </Suspense>
  );
};

function Router({
  hideFooter,
  hideHeader,
  hideSidebar,
}: {
  hideFooter: boolean;
  hideHeader: boolean;
  hideSidebar: boolean;
}) {
  const location = useLocation();
  const isInitialLoadRef = useRef(true);
  const isStationMode = useStationMode();

  /*const isAppNapsterAi =
    typeof window !== 'undefined' &&
    window.location.hostname === 'app.napster.ai';*/

  useEffect(() => {
    isInitialLoadRef.current = false;
  }, [location.pathname]);

  const isInitialLoad = isInitialLoadRef.current;

  const routeKey = useMemo(() => {
    // Twin routes should always use 'twin' key
    if (location.pathname.includes('/twin')) {
      return 'twin';
    }

    // Session routes should always use 'session' key
    // This includes /:slug and /generation/:slug/:taskId routes
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const isGenerationRoute =
      pathSegments[0] === 'generation' && pathSegments.length >= 2;
    const isSlugRoute =
      pathSegments.length === 1 &&
      !KNOWN_NON_SESSION_ROUTES.includes(pathSegments[0]);

    if (isGenerationRoute || isSlugRoute) {
      return 'session';
    }

    return location.pathname;
  }, [location.pathname]);

  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={routeKey}>
        {/* Main Layout Routes - with Sidebar, Header, Footer */}
        <Route
          element={
            <MainLayout
              hideFooter={hideFooter}
              hideHeader={hideHeader}
              hideSidebar={hideSidebar}
            />
          }>
          <Route
            path="/"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <Home />
              </AnimatedPage>
            }
          />
          <Route
            path="/feeds"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <Feeds />
              </AnimatedPage>
            }
          />
          <Route
            path="/favorites"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <ProtectedRoute>
                  <FavoritesPage />
                </ProtectedRoute>
              </AnimatedPage>
            }
          />
          <Route
            path="/playlists"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <ProtectedRoute>
                  <PlaylistsPage />
                </ProtectedRoute>
              </AnimatedPage>
            }
          />
          <Route
            path="/my-creations"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <ProtectedRoute>
                  <MyCreationsPage />
                </ProtectedRoute>
              </AnimatedPage>
            }
          />
          <Route
            path="/musicforyou"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <MusicPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/search"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <SearchPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/companions"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <CompanionsPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/serenity-wellness"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <SerenityWellnessPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/audiobooks"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <WisdomPage />
              </AnimatedPage>
            }
          />
          {Features.enableIndianMusic && (
            <>
              <Route
                path="/indian-music"
                element={
                  <AnimatedPage isInitialLoad={isInitialLoad}>
                    <IndianMusicPage />
                  </AnimatedPage>
                }
              />
              <Route
                path="/indian-music-leaderboard"
                element={
                  <AnimatedPage isInitialLoad={isInitialLoad}>
                    <IndianMusicLeaderboardPage />
                  </AnimatedPage>
                }
              />
            </>
          )}
          {Features.enableLeaderboard && (
            <Route
              path="/leaderboard"
              element={
                <AnimatedPage isInitialLoad={isInitialLoad}>
                  <LeaderboardPage />
                </AnimatedPage>
              }
            />
          )}
          <Route
            path="/old-music"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <ProtectedRoute>
                  <OldMusicsPage />
                </ProtectedRoute>
              </AnimatedPage>
            }
          />
          <Route
            path="/oldplaylists"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad} isFade={true}>
                <OldPlaylistsPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/contributor/:id"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <ContributorProfile />
              </AnimatedPage>
            }
          />
          {isLocalhost && (
            <Route
              path="/icons-dev"
              element={
                <AnimatedPage isInitialLoad={isInitialLoad}>
                  <Suspense fallback={<LoadingFallback />}>
                    <IconsDevPage />
                  </Suspense>
                </AnimatedPage>
              }
            />
          )}
          {/* Campaign landing pages - public, must come before /:slug */}
          <Route path="/offers" element={<Navigate to="/" replace />} />
          <Route path="/offers/:slug" element={<OfferPage />} />
          {/* Auth callback routes - must come before /:slug */}
          <Route path="/callbackSignIn" element={<CallbackSignIn />} />
          <Route path="/callbackSignOut" element={<CallbackSignOut />} />
          <Route path="/auth/partner" element={<PartnerRedirect />} />
          <Route
            path="/auth/partner/success"
            element={<PartnerSubscriptionSuccess />}
          />
          {/* Creation viewer route - for images, artifacts, etc. (public for sharing) */}
          <Route
            path="/creations/:assetType/:orgId/:creationId"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <CreationViewerPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/station/spaces"
            element={
              <ProtectedRoute>
                <StationSpacesPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/generation/:slug/:taskId"
            element={
              <ProtectedRoute>
                {isStationMode ? <StationGenerationPage /> : <SessionPage />}
              </ProtectedRoute>
            }
          />
          {/* Access Denied page route */}
          <Route path="/access-denied" element={<AccessDenied />} />
          <Route path="/player/:albumId/:trackId" element={<></>} />
          <Route path="/playlist/:id/:trackId" element={<></>} />
          <Route
            path="/account/*"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad} isFade={true}>
                <ProtectedRoute>
                  <AccountPage />
                </ProtectedRoute>
              </AnimatedPage>
            }
          />
          <Route
            path="/pricing"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad} isFade>
                <SubscriptionPage />
              </AnimatedPage>
            }
          />
          <Route
            path="/pricing-checkout"
            element={
              <AnimatedPage isInitialLoad={isInitialLoad}>
                <ProtectedRoute>
                  <PricingCheckoutPage />
                </ProtectedRoute>
              </AnimatedPage>
            }
          />
          <Route
            path="/twin/*"
            element={
              <ProtectedRoute>
                <TwinPage key="twin" />
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Session Layout Routes - only Header, no Sidebar/Footer */}
        <Route element={<SessionLayout />}>
          <Route
            path="/:slug"
            element={
              <ProtectedRoute>
                <SessionPage />
              </ProtectedRoute>
            }
          />
        </Route>
      </Routes>
    </AnimatePresence>
  );
}

export default Router;
